import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Calendar, Send, Sparkles, Trash2, RefreshCw, ExternalLink,
  Instagram, Linkedin, Plus, CheckCircle, Clock, Zap, Video,
  Twitter, Globe, FileText, BarChart3
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const fmtDate = (d) => { try { return d ? format(parseISO(d), 'MMM d, h:mm a') : '-'; } catch { return '-'; } };

const PLATFORM_ICONS = {
  instagram: <Instagram size={14} />,
  linkedin: <Linkedin size={14} />,
  twitter: <Twitter size={14} />,
  tiktok: <Video size={14} />,
  facebook: <Globe size={14} />,
};

const PLATFORM_COLORS = {
  instagram: 'bg-pink-50 border-pink-200 text-pink-700',
  linkedin: 'bg-blue-50 border-blue-200 text-blue-700',
  twitter: 'bg-sky-50 border-sky-200 text-sky-700',
  tiktok: 'bg-gray-50 border-gray-200 text-gray-700',
  facebook: 'bg-indigo-50 border-indigo-200 text-indigo-700',
};

const POSTING_SCHEDULE = [
  { day: 'Monday',    type: 'personality_hook',   label: 'Personality Hook',    desc: 'Personal story or perspective', color: 'bg-blue-50 border-blue-200' },
  { day: 'Wednesday', type: 'pov_intel',           label: 'POV Intel',           desc: 'Market intelligence & insight', color: 'bg-teal-50 border-teal-200' },
  { day: 'Friday',    type: 'analysis_breakdown',  label: 'Analysis Breakdown',  desc: 'Data-driven trend analysis',    color: 'bg-amber-50 border-amber-200' },
];

export default function ContentScheduler() {
  const [bufferStatus, setBufferStatus] = useState(null);
  const [queue, setQueue] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queueLoading, setQueueLoading] = useState(false);

  // Composer state
  const [composeText, setComposeText] = useState('');
  const [composeImage, setComposeImage] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('');
  const [scheduleMode, setScheduleMode] = useState('addToQueue');
  const [scheduling, setScheduling] = useState(false);

  // AI generator state
  const [genPlatform, setGenPlatform] = useState('Instagram');
  const [genType, setGenType] = useState('personality_hook');
  const [genContext, setGenContext] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [statusRes, draftsRes] = await Promise.all([
        api.get('/api/buffer/status'),
        api.get('/api/buffer/drafts'),
      ]);
      setBufferStatus(statusRes.data);
      setDrafts(draftsRes.data || []);
      if (statusRes.data?.channels?.length > 0) {
        loadQueue();
      }
    } catch { toast.error('Failed to load Buffer status'); }
    finally { setLoading(false); }
  };

  const loadQueue = async () => {
    setQueueLoading(true);
    try {
      const res = await api.get('/api/buffer/queue');
      setQueue(res.data?.posts || []);
    } catch { } finally { setQueueLoading(false); }
  };

  const schedulePost = async () => {
    if (!composeText.trim()) { toast.error('Post text is required'); return; }
    if (!selectedChannel) { toast.error('Select a channel'); return; }
    setScheduling(true);
    try {
      await api.post('/api/buffer/schedule', {
        channel_id: selectedChannel,
        text: composeText,
        image_url: composeImage || null,
        schedule_mode: scheduleMode,
      });
      toast.success('Post scheduled in Buffer!');
      setComposeText('');
      setComposeImage('');
      loadQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to schedule');
    } finally { setScheduling(false); }
  };

  const generateContent = async () => {
    setGenerating(true);
    setGeneratedContent('');
    try {
      const res = await api.post('/api/buffer/generate-content', {
        platform: genPlatform,
        post_type: genType,
        context: genContext,
      });
      setGeneratedContent(res.data.content);
    } catch { toast.error('Failed to generate content'); }
    finally { setGenerating(false); }
  };

  const useGeneratedContent = () => {
    setComposeText(generatedContent.split('\n\n')[0] || generatedContent.slice(0, 280));
    toast.success('Content copied to composer');
  };

  const deleteFromQueue = async (postId) => {
    try {
      await api.delete(`/api/buffer/queue/${postId}`);
      toast.success('Removed from queue');
      loadQueue();
    } catch { toast.error('Failed to remove'); }
  };

  const saveDraft = async () => {
    if (!composeText.trim()) return;
    try {
      await api.post('/api/buffer/drafts', {
        title: `Draft - ${new Date().toLocaleDateString()}`,
        platform: genPlatform,
        text: composeText,
        image_url: composeImage || null,
        post_type: genType,
      });
      toast.success('Draft saved');
      const r = await api.get('/api/buffer/drafts');
      setDrafts(r.data || []);
    } catch { toast.error('Failed to save draft'); }
  };

  const deleteDraft = async (id) => {
    try {
      await api.delete(`/api/buffer/drafts/${id}`);
      setDrafts(prev => prev.filter(d => d.id !== id));
      toast.success('Draft deleted');
    } catch {}
  };

  const channels = bufferStatus?.channels || [];
  const hasChannels = channels.length > 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Calendar size={26} className="text-primary" />
            Content Scheduler
          </h1>
          <p className="page-subtitle">
            Buffer connected · {channels.length} channel{channels.length !== 1 ? 's' : ''} ·
            Schedule Mon/Wed/Fri using the Hamptons XP playbook
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={loadAll} variant="outline" size="sm"><RefreshCw size={13} className="mr-1.5" />Refresh</Button>
          <a href="https://buffer.com/app" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm"><ExternalLink size={13} className="mr-1.5" />Buffer Dashboard</Button>
          </a>
        </div>
      </div>

      {/* Buffer status bar */}
      <div className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border ${
        hasChannels ? 'bg-teal-50 border-teal-200' : 'bg-amber-50 border-amber-200'
      }`}>
        <span className={`w-2.5 h-2.5 rounded-full ${hasChannels ? 'bg-teal-500' : 'bg-amber-400'}`} />
        <span className={`text-xs font-semibold ${hasChannels ? 'text-teal-700' : 'text-amber-700'}`}>
          {hasChannels ? `Buffer Active — ${bufferStatus?.email}` : 'Buffer Connected — No channels linked yet'}
        </span>
        {!hasChannels && (
          <span className="text-xs text-muted-foreground">
            Connect Instagram, TikTok, LinkedIn at buffer.com → Channels
          </span>
        )}
        {channels.map(ch => (
          <div key={ch.id} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${PLATFORM_COLORS[ch.service] || 'bg-muted border-border text-muted-foreground'}`}>
            {PLATFORM_ICONS[ch.service] || <Globe size={11} />}
            {ch.displayName || ch.name}
          </div>
        ))}
        {!hasChannels && (
          <a href="https://buffer.com/app" target="_blank" rel="noopener noreferrer" className="ml-auto">
            <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700">
              Connect Channels
            </Button>
          </a>
        )}
      </div>

      {/* Posting schedule reference */}
      <div className="grid grid-cols-3 gap-3">
        {POSTING_SCHEDULE.map(s => (
          <div key={s.day} className={`p-3 rounded-xl border ${s.color}`}>
            <div className="font-semibold text-sm text-foreground">{s.day}</div>
            <div className="font-medium text-xs text-primary mt-0.5">{s.label}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{s.desc}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="compose">
        <TabsList className="mb-4">
          <TabsTrigger value="compose" className="flex items-center gap-1.5"><Send size={13} />Compose</TabsTrigger>
          <TabsTrigger value="generate" className="flex items-center gap-1.5"><Sparkles size={13} />AI Generate</TabsTrigger>
          <TabsTrigger value="queue" className="flex items-center gap-1.5">
            <Calendar size={13} />Queue {queue.length > 0 && `(${queue.length})`}
          </TabsTrigger>
          <TabsTrigger value="drafts" className="flex items-center gap-1.5">
            <FileText size={13} />Drafts {drafts.length > 0 && `(${drafts.length})`}
          </TabsTrigger>
        </TabsList>

        {/* ── COMPOSE ── */}
        <TabsContent value="compose">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base" style={{ fontFamily: 'Cormorant Garamond,serif' }}>
                  Schedule a Post
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Channel</Label>
                  {hasChannels ? (
                    <Select value={selectedChannel} onValueChange={setSelectedChannel}>
                      <SelectTrigger data-testid="channel-select">
                        <SelectValue placeholder="Select channel" />
                      </SelectTrigger>
                      <SelectContent>
                        {channels.map(ch => (
                          <SelectItem key={ch.id} value={ch.id}>
                            <span className="flex items-center gap-2">
                              {PLATFORM_ICONS[ch.service]}
                              {ch.displayName || ch.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="p-3 rounded-lg bg-muted/50 border border-dashed border-border text-sm text-muted-foreground">
                      No channels connected. <a href="https://buffer.com/app" target="_blank" rel="noopener noreferrer" className="text-primary underline">Connect channels in Buffer →</a>
                    </div>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label>Post Text</Label>
                  <Textarea
                    value={composeText}
                    onChange={e => setComposeText(e.target.value)}
                    placeholder="Write your post... Use AI Generate tab to get content ideas"
                    rows={5}
                    data-testid="compose-text"
                  />
                  <div className="text-xs text-muted-foreground text-right">{composeText.length} chars</div>
                </div>

                <div className="space-y-1.5">
                  <Label>Image URL (optional)</Label>
                  <Input
                    value={composeImage}
                    onChange={e => setComposeImage(e.target.value)}
                    placeholder="https://..."
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Schedule Mode</Label>
                  <Select value={scheduleMode} onValueChange={setScheduleMode}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="addToQueue">Add to Queue (next slot)</SelectItem>
                      <SelectItem value="now">Post Now</SelectItem>
                      <SelectItem value="prioritize">Prioritize (top of queue)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={schedulePost}
                    disabled={scheduling || !hasChannels}
                    className="flex-1 bg-primary"
                    data-testid="schedule-post-btn"
                  >
                    <Send size={14} className="mr-1.5" />
                    {scheduling ? 'Scheduling...' : 'Schedule in Buffer'}
                  </Button>
                  <Button variant="outline" onClick={saveDraft} disabled={!composeText.trim()}>
                    Save Draft
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quick tips */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base" style={{ fontFamily: 'Cormorant Garamond,serif' }}>
                  Hamptons XP Content Playbook
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  {[
                    { day: 'Mon', label: 'Personality Hook', tip: 'Start with "I was walking through East Hampton and realized..." — personal story that positions you as an insider.' },
                    { day: 'Wed', label: 'POV Intel', tip: 'Share a specific market insight: "3 restaurants that opened on Main St in the last 30 days and what it means for summer..."' },
                    { day: 'Fri', label: 'Analysis Breakdown', tip: 'Data post: "A Hamptons restaurant with 80 seats at $95 avg check and 2.5 turns generates $570K/month in season."' },
                  ].map(item => (
                    <div key={item.day} className="p-3 rounded-lg bg-muted/40 space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">{item.day}</Badge>
                        <span className="text-xs font-semibold text-foreground">{item.label}</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{item.tip}</p>
                    </div>
                  ))}
                </div>
                <Separator />
                <div className="text-xs text-muted-foreground">
                  <p className="font-semibold text-foreground mb-1">Best posting times (Hamptons summer):</p>
                  <p>• Instagram: 7–9 AM or 6–9 PM</p>
                  <p>• LinkedIn: Tue–Thu, 8–10 AM</p>
                  <p>• TikTok: 6–10 PM, everyday</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── AI GENERATE ── */}
        <TabsContent value="generate">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base" style={{ fontFamily: 'Cormorant Garamond,serif' }}>
                  AI Content Generator
                </CardTitle>
                <p className="text-sm text-muted-foreground">Perplexity generates Hamptons-specific content ideas based on the playbook</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Platform</Label>
                    <Select value={genPlatform} onValueChange={setGenPlatform}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Instagram">Instagram</SelectItem>
                        <SelectItem value="TikTok">TikTok</SelectItem>
                        <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                        <SelectItem value="Twitter/X">Twitter/X</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Post Type</Label>
                    <Select value={genType} onValueChange={setGenType}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="personality_hook">Mon · Personality Hook</SelectItem>
                        <SelectItem value="pov_intel">Wed · POV Intel</SelectItem>
                        <SelectItem value="analysis_breakdown">Fri · Analysis</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Additional Context (optional)</Label>
                  <Textarea
                    value={genContext}
                    onChange={e => setGenContext(e.target.value)}
                    placeholder="e.g. New restaurant openings in Montauk, revenue data from last month, upcoming polo season..."
                    rows={3}
                  />
                </div>

                <Button
                  onClick={generateContent}
                  disabled={generating}
                  className="w-full bg-primary"
                  data-testid="generate-content-btn"
                >
                  <Sparkles size={14} className="mr-1.5" />
                  {generating ? 'Generating with Perplexity...' : 'Generate Content'}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base" style={{ fontFamily: 'Cormorant Garamond,serif' }}>
                  Generated Content
                </CardTitle>
              </CardHeader>
              <CardContent>
                {generating ? (
                  <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground text-sm">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    Perplexity is crafting your Hamptons content...
                  </div>
                ) : generatedContent ? (
                  <div className="space-y-3">
                    <ScrollArea className="h-64 pr-2">
                      <div className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                        {generatedContent}
                      </div>
                    </ScrollArea>
                    <Separator />
                    <div className="flex gap-2">
                      <Button onClick={useGeneratedContent} className="flex-1 bg-primary" size="sm">
                        <Send size={13} className="mr-1.5" /> Use in Composer
                      </Button>
                      <Button variant="outline" size="sm" onClick={generateContent}>
                        <RefreshCw size={13} className="mr-1.5" /> Regenerate
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-48 text-center">
                    <Sparkles size={32} className="text-muted-foreground/30 mb-3" />
                    <p className="text-sm text-muted-foreground">Generated content will appear here</p>
                    <p className="text-xs text-muted-foreground mt-1">Select platform + type then click Generate</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── QUEUE ── */}
        <TabsContent value="queue">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-muted-foreground">{queue.length} posts scheduled in Buffer</p>
            <Button onClick={loadQueue} variant="outline" size="sm" disabled={queueLoading}>
              <RefreshCw size={13} className={`mr-1.5 ${queueLoading ? 'animate-spin' : ''}`} />Refresh
            </Button>
          </div>
          {!hasChannels ? (
            <Card><CardContent className="text-center py-12">
              <Calendar size={36} className="mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">Connect social channels in Buffer to see the queue</p>
              <a href="https://buffer.com/app" target="_blank" rel="noopener noreferrer">
                <Button className="mt-3 bg-primary" size="sm"><ExternalLink size={13} className="mr-1.5" />Open Buffer</Button>
              </a>
            </CardContent></Card>
          ) : queue.length === 0 ? (
            <Card><CardContent className="text-center py-12">
              <CheckCircle size={36} className="mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">Queue is empty — schedule some posts!</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-2">
              {queue.map((post, i) => (
                <Card key={post.id} className="hover:shadow-sm transition-shadow">
                  <CardContent className="py-3 px-4 flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="secondary" className="text-xs">#{i + 1}</Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock size={10} /> {fmtDate(post.dueAt)}
                        </span>
                        <span className="badge-on-track text-xs px-2 py-0.5 rounded-full font-medium">
                          {post.status}
                        </span>
                      </div>
                      <p className="text-sm text-foreground">{post.text?.slice(0, 150)}{post.text?.length > 150 ? '...' : ''}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="w-7 h-7 text-muted-foreground hover:text-destructive flex-shrink-0"
                      onClick={() => deleteFromQueue(post.id)}>
                      <Trash2 size={13} />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── DRAFTS ── */}
        <TabsContent value="drafts">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-muted-foreground">{drafts.length} saved drafts</p>
          </div>
          {drafts.length === 0 ? (
            <Card><CardContent className="text-center py-12">
              <FileText size={36} className="mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">No drafts yet — save posts from the Compose tab</p>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {drafts.map(d => (
                <Card key={d.id} className="hover:shadow-sm transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-sm">{d.title}</CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">{d.platform}</Badge>
                          {d.post_type && <Badge variant="secondary" className="text-xs">{d.post_type.replace('_', ' ')}</Badge>}
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" className="w-7 h-7 text-muted-foreground hover:text-destructive"
                        onClick={() => deleteDraft(d.id)}>
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-muted-foreground line-clamp-3">{d.text}</p>
                    <Button size="sm" variant="outline" className="mt-2 w-full"
                      onClick={() => { setComposeText(d.text); setComposeImage(d.image_url || ''); }}>
                      Load into Composer
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
